import React from 'react';
import { StyleSheet, View } from 'react-native';

import { BodyText, Container, DisplayText, theme, useResponsive } from '@sg/garnish';

import { type HomeProductCategory } from '../../hooks';
import { HomeCategory } from '../HomeCategory';
import { HomeContainer } from '../HomeContainer';
import { HomeFilterChip } from '../HomeFilterChip';
import { HomeProduct } from '../HomeProduct';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeFilterableCategories = (props: HomeFilterableCategoriesProps) => {
  const {
    title,
    filterableCategories,
    selectedCategory,
    onSelectCategory,
    openMenu,
    openProductDetails,
    addProductToBag,
  } = props;
  const { currentBreakpoint, match } = useResponsive();

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Container style={match([styles.containerXS, styles.containerSM])}>
      {currentBreakpoint.isXS ? (
        <BodyText bold sizeMatch={['18']}>
          {title}
        </BodyText>
      ) : (
        <DisplayText style={styles.titleSM} sizeMatch={['32']}>
          {title}
        </DisplayText>
      )}

      <View style={styles.filters}>
        {filterableCategories.map((category) => (
          <HomeFilterChip
            key={category.id}
            category={category}
            isSelected={selectedCategory?.categoryName === category.categoryName}
            onPress={onSelectCategory}
          />
        ))}
      </View>

      {selectedCategory ? (
        <HomeContainer.AnimatedContent>
          <HomeCategory.FlexView style={styles.selectedCategory}>
            {selectedCategory?.products?.map((product) => (
              <HomeProduct
                key={product.id}
                id={product.id}
                slug={product.slug}
                title={product.title}
                cost={product.cost}
                description={product.description}
                contentfulImage={product.image}
                isModifiable={product.isModifiable}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={addProductToBag}
              />
            ))}
          </HomeCategory.FlexView>
        </HomeContainer.AnimatedContent>
      ) : null}
    </Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  containerSM: {
    alignItems: 'center',
    gap: theme.spacing['8'],
  },
  titleSM: {
    color: theme.colors.KALE,
    textAlign: 'center',
  },
  filters: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['2'],
  },
  selectedCategory: {
    paddingVertical: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeFilterableCategoriesProps = {
  title: string;
  filterableCategories: HomeProductCategory[];
  selectedCategory: HomeProductCategory | null;
  onSelectCategory: (category: HomeProductCategory | null) => void;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
};
